import { useMemo, type MouseEvent, useState } from 'react';
import {
  LifecycleStatusTag,
  workspaceLifecycleStatus,
} from 'components/lifecycle-status-tag';
import { Button, Dropdown, InlineNotification, Link } from '@carbon/react';
import { Location } from '@carbon/react/icons';
import { useAtomValue } from 'jotai';
import type { SortingState } from '@tanstack/react-table';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { FormattedCount } from 'components/formatted-count';
import { useHistory } from 'react-router-dom';
import { useWorkspaces } from 'pages/well-workspaces/hooks';
import type { ListPaging } from 'pages/well-workspaces/api';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import { wellWorkspacePath } from 'pages/well-workspaces/utils';
import type { WellWorkspaceListItem } from 'types/well-workspace';
import {
  SUPPRESS_BOUNDS_SEARCH,
  combineText,
  globalTextAtom,
  lastSpatialFilterAtom,
  mapSearchRefAtom,
  type WellsResultsView,
} from '../atoms';
import styles from './results-section.module.scss';

// Mock v11's "View" dropdown at the right of the Wells table's toolbar: the wells the
// map is filtered to, or the well workspaces. The mock also lists DCA Workspaces and
// DCA Forecasts; DCA creation is out of Epic 1's scope, so those wait for it.
const VIEWS: { id: WellsResultsView; label: string }[] = [
  { id: 'wells', label: 'Wells' },
  { id: 'workspaces', label: 'Workspaces' },
];

interface DropdownProps {
  value: WellsResultsView;
  onChange: (view: WellsResultsView) => void;
}

export const ResultsViewDropdown = ({ value, onChange }: DropdownProps) => (
  <Dropdown
    id="wells-results-view"
    className={styles.viewDropdown}
    // Same height as the Create menu button beside it, so the two read as one row.
    size="lg"
    // No visible label: the field alone reads as the view switch (the value says
    // "Wells" or "Workspaces"); the title stays for screen readers.
    titleText="View"
    hideLabel
    label="View"
    items={VIEWS}
    itemToString={(item) => item?.label ?? ''}
    selectedItem={VIEWS.find((view) => view.id === value) ?? VIEWS[0]}
    onChange={({ selectedItem }) => {
      if (selectedItem) onChange(selectedItem.id);
    }}
  />
);

// The table's rows must be indexable; the list item is a closed interface.
type WorkspaceRow = WellWorkspaceListItem & Record<string, unknown>;

// Column id → the endpoint's sort attribute. Only these are allowlisted server-side; a
// column absent here (Actions) sorts on nothing and leaves the listing's default order,
// so the two lists cannot drift into a header that sorts silently by created_at.
const SORT_ATTRIBUTES: Record<string, string> = {
  name: 'name',
  wellsCount: 'wells_count',
  promoted: 'promoted',
  promotedBy: 'promoted_by',
  createdBy: 'created_by',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
};

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

interface Props {
  view: WellsResultsView;
  onViewChange: (view: WellsResultsView) => void;
  // The search text — the SAME applied text the wells table's own search box reads
  // and writes (the wells dataset's datasetTextAtom, via ResultsSection's
  // datasetText/handleSearchChange) — not a copy. Sharing the one value, rather than
  // seeding a local copy, is what makes it survive the View dropdown switching either
  // way (Wells→Workspaces and back), not just carry over once on mount.
  text: string;
  onTextChange: (value: string) => void;
}

// The Workspaces view of the Wells results panel: the same table shell as the wells —
// its toolbar (the View dropdown ahead of the column and reset controls), search,
// sorting, column visibility — over every well workspace, in the listing page's
// columns. A name opens the workspace at its own URL, like every workspace here.
// Mounted only while selected, so the list is read only when someone asks to see it.
export const WorkspacesResults = ({
  view,
  onViewChange,
  text,
  onTextChange,
}: Props) => {
  // The header/global search text. The wells dataset already searches on
  // combineText(global, dataset); the workspaces view reads the same global text so
  // the top search filters this listing too (its own box still shows only `text`).
  const globalText = useAtomValue(globalTextAtom);
  // The applied query sent to the server: the header/global text merged with this view's
  // own box text. Both boxes stay independent for display; only the query text merges.
  const effectiveText = combineText(globalText, text) ?? '';
  // Paged and sorted through the server, so a listing past the first page is
  // reachable and the header sort acts on every workspace rather than on the one
  // page the browser holds; the envelope's total drives the pager. Search stays
  // out of this component's own state — see `text`/`onTextChange` above.
  const [paging, setPaging] = useState<ListPaging>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // The table sorts one column at a time; a column the endpoint cannot order by maps to
  // nothing and the listing keeps its default order.
  const activeSort = sorting[0];
  const sortAttribute = activeSort ? SORT_ATTRIBUTES[activeSort.id] : undefined;
  const sortDirection = activeSort?.desc ? 'DESC' : 'ASC';

  // The map filter the datasets are already bounded to — the visible extent, or the drawn
  // AOI polygon. Reading it here is what makes the Workspaces view answer "the workspaces
  // in view" rather than "the newest workspaces anywhere", so a row's "Show on map" lands
  // where the reader is already looking.
  const spatialFilter = useAtomValue(lastSpatialFilterAtom);
  const boundingBox = spatialFilter.bounding_box;
  const polygonGeojson = spatialFilter.polygon_geojson;

  // Panning the map is a new result set, and the page the reader was on may not exist in
  // it. Keyed on the shape's values rather than its identity, since each broadcast builds
  // a fresh object.
  const spatialKey = JSON.stringify(polygonGeojson ?? boundingBox ?? null);
  const [lastSpatialKey, setLastSpatialKey] = useState(spatialKey);
  const [lastSearchKey, setLastSearchKey] = useState(effectiveText);

  // Reset during render rather than in an effect. An effect would leave this render
  // holding the old page against the new area, and that render is what issues the read —
  // so panning from page 3 fired a doomed page-3 request (blanking the table for a beat)
  // before the reset queued a second one. React re-runs this component immediately on a
  // render-phase update and discards the first pass, so only the page-1 read is ever made.
  let activePaging = paging;
  if (spatialKey !== lastSpatialKey) {
    setLastSpatialKey(spatialKey);
    if (paging.page !== 1) {
      activePaging = { ...paging, page: 1 };
      setPaging(activePaging);
    }
  }

  // A changed search (this view's box OR the merged-in header/global text) is a new
  // result set too — reset to page 1 the same render, so we never issue a doomed
  // page-N read against a smaller filtered set.
  if (effectiveText !== lastSearchKey) {
    setLastSearchKey(effectiveText);
    if (activePaging.page !== 1) {
      activePaging = { ...activePaging, page: 1 };
      setPaging(activePaging);
    }
  }

  const workspaces = useWorkspaces({
    ...activePaging,
    text: effectiveText,
    sortAttribute,
    sortDirection,
    boundingBox,
    polygonGeojson,
  });
  const history = useHistory();
  const mapRef = useAtomValue(mapSearchRefAtom);

  const columns = useMemo<DataTableColumnDef<WorkspaceRow>[]>(
    () => [
      {
        // "Show on map", as the wells rows have it: the map fits to the extent of the
        // workspace's wells. The extent is the backend's `mapBounds` on the list item
        // (the same shape the wells rows carry); until an item has one — the field
        // is being added — its button is disabled rather than doing nothing.
        id: 'actions',
        header: '',
        size: 70,
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => {
          const bounds = row.original.mapBounds ?? null;
          return (
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Location}
              iconDescription={
                bounds
                  ? 'Show on map'
                  : 'Show on map — location not available for this workspace'
              }
              hasIconOnly
              disabled={!bounds}
              onClick={() => {
                if (!bounds || !mapRef) return;
                // Tagged so visible-extent mode does not rebound the search on the
                // move, the same way the wells' fly-to is. A one-well workspace's extent
                // is a point, and fitting a point zooms to the map's ceiling (a 1 m
                // scale) — capped at the wells' own fly-to zoom instead.
                mapRef.fitBounds(
                  bounds,
                  { padding: 48, duration: 800, maxZoom: 14 },
                  SUPPRESS_BOUNDS_SEARCH
                );
              }}
            />
          );
        },
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Workspace',
        size: 320,
        cell: ({ row }) => (
          // The href is the workspace's own URL (new tab, bookmark, copied link); a plain
          // click goes there client-side so the app shell stays mounted.
          <Link
            href={wellWorkspacePath(row.original.id)}
            onClick={(event: MouseEvent) => {
              event.preventDefault();
              history.push(wellWorkspacePath(row.original.id));
            }}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'wellsCount',
        accessorKey: 'wellsCount',
        header: 'Wells',
        size: 100,
        cell: ({ row }) => <FormattedCount value={row.original.wellsCount} />,
      },
      {
        id: 'promoted',
        accessorKey: 'promoted',
        header: 'Promoted',
        size: 120,
        cell: ({ row }) => (
          <LifecycleStatusTag
            status={workspaceLifecycleStatus(row.original.promoted)}
          />
        ),
      },
      {
        id: 'promotedBy',
        accessorKey: 'promotedBy',
        header: 'Promoted By',
        size: 160,
        cell: ({ row }) =>
          row.original.promotedBy ?? (row.original.promoted ? 'System' : '—'),
      },
      {
        id: 'createdBy',
        accessorKey: 'createdBy',
        header: 'Created By',
        size: 160,
        cell: ({ row }) => row.original.createdBy ?? '—',
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Created',
        size: 130,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: 'Last Updated',
        size: 130,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    [history, mapRef]
  );

  if (workspaces.isError) {
    return (
      <div className={styles.container}>
        <div className={styles.viewToolbar}>
          <ResultsViewDropdown value={view} onChange={onViewChange} />
        </div>
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Error"
          subtitle="Could not load the well workspaces."
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <DataTable<WorkspaceRow>
        data={(workspaces.data?.rows ?? []) as WorkspaceRow[]}
        columns={columns}
        getRowId={(row) => String(row.id)}
        isLoading={workspaces.isPending}
        persistenceKey="map-query-workspaces"
        // Sorting and search are server-side; the endpoint takes no per-column filter
        // params, so that one stays off rather than dangling as dead UI. Multi-sort is
        // off because only the first column is sent: left on, a shift-click would draw a
        // second sort indicator the ordering does not reflect.
        config={{ sorting: true, multiSort: false, columnFilters: false }}
        // All three server-driven slices are controlled together. globalFilter especially:
        // it otherwise lives only in the table's own reducer, which the error branch below
        // discards by unmounting the table — leaving the search box empty while the
        // listing was still filtered by it.
        controlledState={{
          pagination: activePaging,
          sorting,
          globalFilter: text,
        }}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: workspaces.data?.totalCount ?? 0,
        }}
        // All three take their paging from the snapshot rather than re-deriving it. A new
        // sort or search re-reads from the first page — the row that was on page 3 under
        // the old order is not the row there under the new one — and the snapshot already
        // carries that reset, so reading it keeps the rule in one place instead of
        // restating a `page: 1` literal per handler.
        onPaginationChange={(state: TableState) =>
          setPaging({ page: state.page, pageSize: state.pageSize })
        }
        onSortChange={(state: TableState) => {
          setSorting(state.sorting);
          setPaging({ page: state.page, pageSize: state.pageSize });
        }}
        // The toolbar's search input debounces on its own, so this is already the
        // settled query. Written through the shared applied-text setter (not local
        // state) so typing here also updates what the wells table shows/sends.
        onGlobalSearchChange={(state: TableState) => {
          onTextChange(state.globalFilter);
          setPaging({ page: state.page, pageSize: state.pageSize });
        }}
        searchPlaceholder="Search workspaces"
        leftToolbarActions={[
          <ResultsViewDropdown
            key="wells-results-view"
            value={view}
            onChange={onViewChange}
          />,
        ]}
      />
    </div>
  );
};
